/**
 * Quiz Activity Component
 * Multi-question quiz with optional time limit
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../ui/button';
import { CheckCircle, Clock, ChevronLeft, ChevronRight, AlertCircle, Trophy, Sparkles, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import type { ActivitySlideProps } from '../types';
import { stripHtml } from '../../../../utils/slides/stripHtml';
import { createDeterministicShuffledOrder } from '../../../../utils/slides/shuffle';

const OPTION_COLORS = [
  { bg: 'bg-blue-500', bgLight: 'bg-blue-500/10', border: 'border-blue-500', ring: 'ring-blue-500/30', text: 'text-blue-600' },
  { bg: 'bg-orange-500', bgLight: 'bg-orange-500/10', border: 'border-orange-500', ring: 'ring-orange-500/30', text: 'text-orange-600' },
  { bg: 'bg-emerald-500', bgLight: 'bg-emerald-500/10', border: 'border-emerald-500', ring: 'ring-emerald-500/30', text: 'text-emerald-600' },
  { bg: 'bg-purple-500', bgLight: 'bg-purple-500/10', border: 'border-purple-500', ring: 'ring-purple-500/30', text: 'text-purple-600' },
];

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
}

export default function Quiz({
  slide,
  onSubmit,
  onAnswerChange,
  selectedAnswer,
  hasSubmitted,
  shuffleSeed,
  className = '',
}: ActivitySlideProps) {
  const { content } = slide;

  // Get questions from content
  const questions: QuizQuestion[] = content?.questions || [];
  const configuredTimeLimit = Number(content?.timeLimit);
  const timeLimit = Number.isFinite(configuredTimeLimit) && configuredTimeLimit > 0
    ? configuredTimeLimit
    : 60;
  const totalQuestions = questions.length;
  const questionOrder = useMemo(
    () => content?.shuffleQuestions
      ? createDeterministicShuffledOrder(totalQuestions, `${shuffleSeed ?? 'default'}:${slide.id}:quiz-questions`)
      : questions.map((_, index) => index),
    [content?.shuffleQuestions, questions, shuffleSeed, slide.id, totalQuestions]
  );

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [showWarning, setShowWarning] = useState(false);

  const currentOriginalQuestionIndex = questionOrder[currentQuestionIndex] ?? 0;
  const currentQuestion = questions[currentOriginalQuestionIndex];

  // Timer countdown (if time limit is set)
  useEffect(() => {
    if (timeLimit > 0 && timeLeft > 0 && !hasSubmitted) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
    // Auto-submit when time runs out
    if (timeLimit > 0 && timeLeft === 0 && !hasSubmitted) {
      handleSubmit();
    }
  }, [timeLeft, hasSubmitted, timeLimit]);

  // Update answer for current question
  const handleAnswerSelect = useCallback((optionIndex: number) => {
    if (hasSubmitted) return;

    const newAnswers = { ...answers, [currentOriginalQuestionIndex]: optionIndex };
    setAnswers(newAnswers);
    onAnswerChange(newAnswers);
  }, [answers, currentOriginalQuestionIndex, hasSubmitted, onAnswerChange]);

  // Navigate to next question
  const handleNext = useCallback(() => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  }, [currentQuestionIndex, totalQuestions]);

  // Navigate to previous question
  const handlePrev = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);

  // Submit all answers
  const handleSubmit = useCallback(() => {
    // Check if all questions are answered
    const unansweredCount = totalQuestions - Object.keys(answers).length;
    if (unansweredCount > 0 && timeLeft > 0) {
      setShowWarning(true);
      return;
    }

    onSubmit(answers);
  }, [answers, totalQuestions, timeLeft, onSubmit]);

  // Force submit after warning confirmation
  const handleForceSubmit = useCallback(() => {
    setShowWarning(false);
    onSubmit(answers);
  }, [answers, onSubmit]);

  // Calculate progress
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / totalQuestions) * 100;

  // If no questions configured
  if (questions.length === 0) {
    return (
      <div className={`w-full ${className}`}>
        <div className="bg-muted/30 rounded-3xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No questions configured for this quiz.</p>
        </div>
      </div>
    );
  }

  // Submitted state
  if (hasSubmitted) {
    const correctCount = questions.filter((q, i) => answers[i] === q.correctAnswer).length;
    const score = Math.round((correctCount / totalQuestions) * 100);

    return (
      <div className={`w-full ${className}`}>
        <div className="bg-gradient-to-br from-blue-500/5 via-primary/5 to-accent/5 rounded-3xl p-6 sm:p-8 text-center border border-border/40">
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200 }}
          >
            <Trophy className={`w-16 h-16 mx-auto mb-4 ${
              score >= 70 ? 'text-yellow-500' : 'text-muted-foreground'
            }`} />
          </motion.div>
          <h2 className="text-2xl font-extrabold mb-2">Quiz Complete!</h2>
          <p className="text-base text-muted-foreground mb-5">
            {correctCount} out of {totalQuestions} correct
          </p>
          
          {/* Score circle */}
          <div className="relative w-24 h-24 mx-auto mb-6">
            <svg className="w-24 h-24 transform -rotate-90">
              <circle cx="48" cy="48" r="42" stroke="currentColor" strokeWidth="6" fill="none" className="text-muted/50" />
              <motion.circle
                cx="48" cy="48" r="42"
                stroke="currentColor"
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
                className={score >= 70 ? 'text-accent' : 'text-yellow-500'}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: score / 100 }}
                transition={{ duration: 1, delay: 0.3 }}
                style={{ strokeDasharray: '264', strokeDashoffset: 0 }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-2xl font-extrabold ${score >= 70 ? 'text-accent' : 'text-yellow-500'}`}>{score}%</span>
            </div>
          </div>

          {/* Question review */}
          <div className="mt-6 space-y-3 max-w-2xl mx-auto text-left">
            <h3 className="font-semibold text-center mb-3 text-sm uppercase tracking-wide text-muted-foreground">Review</h3>
            {questionOrder.map((originalQuestionIndex, i) => {
              const q = questions[originalQuestionIndex];
              const userAnswer = answers[originalQuestionIndex];
              const isCorrect = userAnswer === q.correctAnswer;
              
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className={`p-4 rounded-xl border-2 ${
                    isCorrect ? 'border-accent/30 bg-accent/5' : 'border-destructive/30 bg-destructive/5'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                      isCorrect ? 'bg-accent text-white' : 'bg-destructive text-white'
                    }`}>
                      {isCorrect ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <span className="text-xs font-bold">✕</span>}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium mb-1 text-sm">Q{i + 1}: {stripHtml(q.question)}</p>
                      <div className="space-y-1 text-xs">
                        <p className={userAnswer !== undefined ? 'text-foreground' : 'text-destructive'}>
                          <span className="font-medium">Your answer:</span> {
                            userAnswer !== undefined 
                              ? `${String.fromCharCode(65 + userAnswer)}. ${q.options[userAnswer]}`
                              : 'Not answered'
                          }
                        </p>
                        {!isCorrect && (
                          <p className="text-accent">
                            <span className="font-medium">Correct:</span> {
                              String.fromCharCode(65 + q.correctAnswer)
                            }. {q.options[q.correctAnswer]}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
        <div className="bg-gradient-to-br from-blue-500/5 via-primary/5 to-accent/5 rounded-3xl p-4 sm:p-6 max-w-4xl mx-auto border border-border/40">
        {/* Header */}
        <div className="mb-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex items-center gap-2 self-start bg-blue-500/10 text-blue-600 px-4 py-1.5 rounded-full">
              <Sparkles className="w-4 h-4" />
              <span className="font-semibold text-sm">Quiz</span>
            </div>
            {timeLimit > 0 && (
              <div className={`inline-flex items-center gap-2 self-start sm:self-auto px-4 py-1.5 rounded-full font-mono text-sm ${
                timeLeft <= 10 ? 'bg-destructive/15 text-destructive animate-pulse' : 'bg-muted/60'
              }`}>
                <Clock className="w-4 h-4" />
                <span className="font-bold">
                  {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                </span>
              </div>
            )}
          </div>

          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex flex-col gap-1 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
              <span className="font-medium">Question {currentQuestionIndex + 1} of {totalQuestions}</span>
              <span>{answeredCount} answered</span>
            </div>
            <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-primary to-accent rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        </div>

        {/* Current question */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="mb-6"
          >
            <h3 className="text-xl font-bold mb-6">{stripHtml(currentQuestion.question)}</h3>

            {/* Options — Color-coded like Nearpod/Kahoot */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {currentQuestion.options.map((option, index) => {
                const isSelected = answers[currentOriginalQuestionIndex] === index;
                const color = OPTION_COLORS[index % OPTION_COLORS.length];
                
                return (
                  <motion.button
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAnswerSelect(index)}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${
                      isSelected
                        ? `${color.border} ${color.bgLight} shadow-md ring-2 ${color.ring}`
                        : 'border-border/50 bg-card hover:shadow-md hover:border-opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-extrabold text-sm shrink-0 ${
                        isSelected
                          ? `${color.bg} text-white`
                          : `bg-muted/80 ${color.text}`
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </div>
                      <div className="flex-1 font-medium text-sm">{option}</div>
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className={`w-6 h-6 rounded-full ${color.bg} flex items-center justify-center shrink-0`}
                        >
                          <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                        </motion.div>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex flex-col gap-3 pt-5 border-t border-border/40 sm:flex-row sm:items-center sm:justify-between">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentQuestionIndex === 0}
            className="gap-2 rounded-xl w-full sm:w-auto"
            size="sm"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>

          {currentQuestionIndex < totalQuestions - 1 ? (
            <Button
              onClick={handleNext}
              className="gap-2 rounded-xl w-full sm:w-auto"
              size="sm"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              className="gap-2 rounded-xl w-full sm:w-auto bg-accent hover:bg-accent/90 shadow-lg shadow-accent/20"
              size="sm"
            >
              <CheckCircle className="w-4 h-4" />
              Submit Quiz
            </Button>
          )}
        </div>

        {/* Question navigator dots */}
        <div className="flex flex-wrap justify-center gap-2 mt-5 pt-5 border-t border-border/40">
          {questionOrder.map((originalQuestionIndex, i) => (
            <button
              key={i}
              onClick={() => setCurrentQuestionIndex(i)}
              className={`h-9 min-w-9 px-2 rounded-xl font-semibold text-xs transition-all ${
                i === currentQuestionIndex
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : answers[originalQuestionIndex] !== undefined
                  ? 'bg-accent/15 text-accent border border-accent/30'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Warning modal for unanswered questions */}
      <AnimatePresence>
        {showWarning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowWarning(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-3xl p-6 max-w-md shadow-xl"
            >
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-center mb-2">Unanswered Questions</h3>
              <p className="text-muted-foreground text-center mb-6">
                You have {totalQuestions - answeredCount} unanswered question(s). 
                Are you sure you want to submit?
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  variant="outline"
                  onClick={() => setShowWarning(false)}
                  className="flex-1"
                >
                  Go Back
                </Button>
                <Button
                  onClick={handleForceSubmit}
                  className="flex-1 bg-accent hover:bg-accent/90"
                >
                  Submit Anyway
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
